import React, { useState, useEffect, useCallback } from 'react';
import { 
  Activity, RefreshCw, AlertTriangle, CheckCircle, XCircle, 
  HelpCircle, Clock, Zap, TrendingUp, Server
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SourceHealthPanel = ({ token }) => {
  const [healthData, setHealthData] = useState(null);
  const [failingSources, setFailingSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [actionStatus, setActionStatus] = useState(null);

  const fetchHealthData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sources/health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHealthData(data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching health data:', error);
    }
  }, [token]);

  const fetchFailingSources = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/sources/failing`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFailingSources(data.failing_sources || []);
      }
    } catch (error) {
      console.error('Error fetching failing sources:', error);
    }
  }, [token]);

  const refreshData = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([fetchHealthData(), fetchFailingSources()]);
    setIsRefreshing(false);
  }, [fetchHealthData, fetchFailingSources]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchHealthData(), fetchFailingSources()]);
      setIsLoading(false);
    };
    loadData();
    
    // Auto-refresh toutes les 60 secondes
    const interval = setInterval(refreshData, 60000);
    return () => clearInterval(interval);
  }, [fetchHealthData, fetchFailingSources, refreshData]);

  const triggerHealthCheck = async () => {
    setActionStatus({ type: 'info', message: 'Lancement du health check...' });
    try {
      const response = await fetch(`${API_URL}/api/admin/sources/health-check`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setActionStatus({ type: 'success', message: 'Health check lancé en arrière-plan (30-60s)' });
        setTimeout(refreshData, 45000); // Refresh après 45s
      } else if (response.status === 403) {
        setActionStatus({ type: 'error', message: 'Accès réservé aux administrateurs' });
      }
    } catch (error) {
      setActionStatus({ type: 'error', message: 'Erreur: ' + error.message });
    }
    setTimeout(() => setActionStatus(null), 5000);
  };

  const triggerRetry = async () => {
    setActionStatus({ type: 'info', message: 'Réessai des sources en échec...' });
    try {
      const response = await fetch(`${API_URL}/api/admin/sources/retry-failing`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setActionStatus({ type: 'success', message: 'Réessai lancé en arrière-plan' });
        setTimeout(refreshData, 30000);
      } else if (response.status === 403) {
        setActionStatus({ type: 'error', message: 'Accès réservé aux administrateurs' });
      }
    } catch (error) {
      setActionStatus({ type: 'error', message: 'Erreur: ' + error.message });
    }
    setTimeout(() => setActionStatus(null), 5000);
  };

  if (isLoading) {
    return (
      <div className="bg-black border-2 border-marine-cyan/50 p-8 text-center">
        <RefreshCw className="w-8 h-8 text-marine-cyan animate-spin mx-auto" />
        <p className="font-mono text-white/90 mt-4">Chargement des données de santé...</p>
      </div>
    );
  }

  const summary = healthData?.summary || {};
  const pieData = [
    { name: 'Healthy', value: summary.healthy || 0, color: '#22c55e' },
    { name: 'Degraded', value: summary.degraded || 0, color: '#f59e0b' },
    { name: 'Failing', value: summary.failing || 0, color: '#ef4444' },
    { name: 'Unknown', value: summary.unknown || 0, color: '#6b7280' },
  ].filter(d => d.value > 0);

  const categoryData = {};
  if (healthData?.sources) {
    Object.values(healthData.sources).forEach(source => {
      const status = source.status || 'unknown';
      if (!categoryData[status]) {
        categoryData[status] = 0;
      }
      categoryData[status]++;
    });
  }

  const barData = [
    { name: 'OK', count: summary.healthy || 0, fill: '#22c55e' },
    { name: 'Dégradé', count: summary.degraded || 0, fill: '#f59e0b' },
    { name: 'Échec', count: summary.failing || 0, fill: '#ef4444' },
    { name: 'Inconnu', count: summary.unknown || 0, fill: '#6b7280' },
  ];

  const healthPercent = summary.total > 0 
    ? Math.round((summary.healthy / summary.total) * 100) 
    : 0;

  return (
    <div className="bg-black border-2 border-emerald-500/50">
      {/* Header */}
      <div className="bg-emerald-900/30 px-4 py-3 border-b border-emerald-500/50 flex items-center justify-between">
        <span className="font-mono text-emerald-400 text-sm uppercase tracking-wider flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Santé des Sources RSS (v3.0)
        </span>
        <div className="flex items-center gap-2">
          {lastUpdate && (
            <span className="font-mono text-white/80 text-xs">
              MAJ: {lastUpdate.toLocaleTimeString('fr-FR')}
            </span>
          )}
          <button
            onClick={refreshData}
            disabled={isRefreshing}
            className="p-1 text-emerald-400 hover:text-white disabled:opacity-50"
            title="Rafraîchir"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Action Status */}
      {actionStatus && (
        <div className={`px-4 py-2 font-mono text-sm ${
          actionStatus.type === 'success' ? 'bg-green-900/30 text-green-400' :
          actionStatus.type === 'error' ? 'bg-red-900/30 text-red-400' :
          'bg-blue-900/30 text-blue-400'
        }`}>
          {actionStatus.message}
        </div>
      )}

      <div className="p-4 space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="bg-emerald-900/20 border border-emerald-500/30 p-3 text-center">
            <Server className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <p className="font-heading text-emerald-400 text-2xl">{summary.total || 0}</p>
            <p className="font-mono text-white/80 text-xs">TOTAL</p>
          </div>
          <div className="bg-green-900/20 border border-green-500/30 p-3 text-center">
            <CheckCircle className="w-5 h-5 text-green-400 mx-auto mb-1" />
            <p className="font-heading text-green-400 text-2xl">{summary.healthy || 0}</p>
            <p className="font-mono text-white/80 text-xs">HEALTHY</p>
          </div>
          <div className="bg-amber-900/20 border border-amber-500/30 p-3 text-center">
            <AlertTriangle className="w-5 h-5 text-amber-400 mx-auto mb-1" />
            <p className="font-heading text-amber-400 text-2xl">{summary.degraded || 0}</p>
            <p className="font-mono text-white/80 text-xs">DEGRADED</p>
          </div>
          <div className="bg-red-900/20 border border-red-500/30 p-3 text-center">
            <XCircle className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <p className="font-heading text-red-400 text-2xl">{summary.failing || 0}</p>
            <p className="font-mono text-white/80 text-xs">FAILING</p>
          </div>
          <div className="bg-gray-900/20 border border-gray-500/30 p-3 text-center">
            <HelpCircle className="w-5 h-5 text-gray-400 mx-auto mb-1" />
            <p className="font-heading text-gray-400 text-2xl">{summary.unknown || 0}</p>
            <p className="font-mono text-white/80 text-xs">UNKNOWN</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Pie Chart */}
          <div className="bg-marine-slate/10 border border-marine-slate/30 p-4">
            <h4 className="font-mono text-white/90 text-xs uppercase mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Distribution des Statuts
            </h4>
            <div className="h-48 flex items-center">
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: '#1a1a2e', 
                      border: '1px solid #4ade80',
                      fontFamily: 'monospace'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                <div className="text-center mb-4">
                  <p className="font-heading text-4xl text-emerald-400">{healthPercent}%</p>
                  <p className="font-mono text-white/80 text-xs">OPÉRATIONNEL</p>
                </div>
                {pieData.map((entry, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                    <span className="font-mono text-white/90 text-xs">{entry.name}</span>
                    <span className="font-mono text-white text-xs ml-auto">{entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Bar Chart */}
          <div className="bg-marine-slate/10 border border-marine-slate/30 p-4">
            <h4 className="font-mono text-white/90 text-xs uppercase mb-3">
              Répartition par Statut
            </h4>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} layout="vertical">
                  <XAxis type="number" stroke="#ffffff50" fontSize={10} />
                  <YAxis dataKey="name" type="category" stroke="#ffffff50" fontSize={10} width={60} />
                  <Tooltip 
                    contentStyle={{ 
                      background: '#1a1a2e', 
                      border: '1px solid #4ade80',
                      fontFamily: 'monospace',
                      fontSize: '12px'
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                    {barData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Scheduler Info */}
        <div className="bg-blue-900/20 border border-blue-500/30 p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-blue-400" />
            <div>
              <p className="font-mono text-white text-sm">Health Check Automatique</p>
              <p className="font-mono text-white/80 text-xs">
                Réessai des sources HS toutes les 24h à 03:00 UTC
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={triggerRetry}
              className="px-3 py-1.5 bg-amber-900/30 border border-amber-500/50 text-amber-400 hover:bg-amber-900/50 font-mono text-xs uppercase flex items-center gap-1"
              data-testid="retry-failing-btn"
            >
              <Zap className="w-3 h-3" />
              Réessayer HS
            </button>
            <button
              onClick={triggerHealthCheck}
              className="px-3 py-1.5 bg-emerald-900/30 border border-emerald-500/50 text-emerald-400 hover:bg-emerald-900/50 font-mono text-xs uppercase flex items-center gap-1"
              data-testid="health-check-btn"
            >
              <Activity className="w-3 h-3" />
              Health Check
            </button>
          </div>
        </div>

        {/* Failing Sources List */}
        {failingSources.length > 0 && (
          <div className="bg-red-900/10 border border-red-500/30">
            <div className="bg-red-900/20 px-4 py-2 border-b border-red-500/30">
              <span className="font-mono text-red-400 text-xs uppercase flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Sources en Échec ou Dégradées ({failingSources.length})
              </span>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-red-500/20">
              {failingSources.slice(0, 15).map((source, idx) => (
                <div key={idx} className="px-4 py-2 flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-mono text-white text-sm">{source.name}</p>
                    <p className="font-mono text-white/80 text-xs truncate max-w-md">
                      {source.last_error || 'Erreur inconnue'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 text-xs font-mono uppercase ${
                      source.status === 'failing' 
                        ? 'bg-red-900/30 text-red-400' 
                        : 'bg-amber-900/30 text-amber-400'
                    }`}>
                      {source.status}
                    </span>
                    <span className="font-mono text-white/30 text-xs">
                      {source.consecutive_failures}x
                    </span>
                  </div>
                </div>
              ))}
              {failingSources.length > 15 && (
                <div className="px-4 py-2 text-center">
                  <span className="font-mono text-white/80 text-xs">
                    +{failingSources.length - 15} autres sources...
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SourceHealthPanel;
